import { type ReactElement, useState, useMemo } from "react";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/Components/Overlays";
import { Button } from "@/Components/Common/Buttons";
import { Input } from "./Input";
import { LabelField } from "./LabelField";
import { cn } from "@/lib/utils";
import {
    File, FileText, FileSpreadsheet, FileCheck, FileOutput, FileBadge, FileBadge2,
    FileImage, FileArchive, FileCode, FileCog, FileSearch, FileWarning,
    Receipt, ReceiptText,
    Mail, MailOpen, Inbox, Send,
    Truck, Ship, Plane, Car,
    ClipboardCheck, ClipboardList, Clipboard,
    ShieldCheck, Shield,
    Table, Database, BarChart3,
    Wrench, Settings, Cog,
    CreditCard, Wallet, Banknote, CircleDollarSign,
    Package, PackageCheck, PackageOpen,
    User, Users, UserCheck, Handshake, Building2,
    Check, CheckCircle, AlertTriangle, Info, XCircle,
    Tag, Bookmark, Star, Flag,
    Search, Eye, Lock,
    Calendar, Clock,
    Globe, MapPin,
    Camera, Image, Scan,
    Sparkles, Zap, Lightbulb,
    ArrowDownToLine, ArrowUpFromLine,
    type LucideIcon,
} from "lucide-react";

interface IconEntry {
    name: string;
    icon: LucideIcon;
    keywords: string[];
}

const ICON_CATALOG: IconEntry[] = [
    // Files
    { name: "file", icon: File, keywords: ["file", "document", "generic"] },
    { name: "file-text", icon: FileText, keywords: ["file", "document", "text", "pdf"] },
    { name: "file-spreadsheet", icon: FileSpreadsheet, keywords: ["file", "spreadsheet", "excel", "csv"] },
    { name: "file-check", icon: FileCheck, keywords: ["file", "verified", "approved"] },
    { name: "file-output", icon: FileOutput, keywords: ["file", "export", "release"] },
    { name: "file-badge", icon: FileBadge, keywords: ["file", "badge", "registration", "certificate"] },
    { name: "file-badge-2", icon: FileBadge2, keywords: ["file", "badge", "registration", "certificate"] },
    { name: "file-image", icon: FileImage, keywords: ["file", "image", "photo"] },
    { name: "file-archive", icon: FileArchive, keywords: ["file", "archive", "zip"] },
    { name: "file-code", icon: FileCode, keywords: ["file", "code", "technical"] },
    { name: "file-cog", icon: FileCog, keywords: ["file", "settings", "config"] },
    { name: "file-search", icon: FileSearch, keywords: ["file", "search", "find"] },
    { name: "file-warning", icon: FileWarning, keywords: ["file", "warning", "alert"] },
    // Finance
    { name: "receipt", icon: Receipt, keywords: ["receipt", "invoice", "bill", "finance"] },
    { name: "receipt-text", icon: ReceiptText, keywords: ["receipt", "invoice", "credit", "note"] },
    { name: "credit-card", icon: CreditCard, keywords: ["credit", "card", "payment"] },
    { name: "wallet", icon: Wallet, keywords: ["wallet", "payment", "money"] },
    { name: "banknote", icon: Banknote, keywords: ["banknote", "money", "cash"] },
    { name: "circle-dollar-sign", icon: CircleDollarSign, keywords: ["dollar", "money", "currency"] },
    // Communication
    { name: "mail", icon: Mail, keywords: ["mail", "email", "message"] },
    { name: "mail-open", icon: MailOpen, keywords: ["mail", "email", "read"] },
    { name: "inbox", icon: Inbox, keywords: ["inbox", "mail", "receive"] },
    { name: "send", icon: Send, keywords: ["send", "submit", "deliver"] },
    // Transport
    { name: "truck", icon: Truck, keywords: ["truck", "transport", "cmr", "delivery", "logistics"] },
    { name: "ship", icon: Ship, keywords: ["ship", "maritime", "sea"] },
    { name: "plane", icon: Plane, keywords: ["plane", "air", "flight"] },
    { name: "car", icon: Car, keywords: ["car", "vehicle", "auto"] },
    // Inspection / Verification
    { name: "clipboard-check", icon: ClipboardCheck, keywords: ["clipboard", "check", "inspection", "report"] },
    { name: "clipboard-list", icon: ClipboardList, keywords: ["clipboard", "list", "checklist"] },
    { name: "clipboard", icon: Clipboard, keywords: ["clipboard", "copy", "paste"] },
    { name: "shield-check", icon: ShieldCheck, keywords: ["shield", "check", "coc", "verified", "secure"] },
    { name: "shield", icon: Shield, keywords: ["shield", "security", "protect"] },
    // Data
    { name: "table", icon: Table, keywords: ["table", "spreadsheet", "data", "grid"] },
    { name: "database", icon: Database, keywords: ["database", "storage", "data"] },
    { name: "bar-chart-3", icon: BarChart3, keywords: ["chart", "analytics", "report"] },
    // Tools
    { name: "wrench", icon: Wrench, keywords: ["wrench", "service", "repair", "tool"] },
    { name: "settings", icon: Settings, keywords: ["settings", "gear", "config"] },
    { name: "cog", icon: Cog, keywords: ["cog", "gear", "settings"] },
    // Packages
    { name: "package", icon: Package, keywords: ["package", "box", "delivery"] },
    { name: "package-check", icon: PackageCheck, keywords: ["package", "verified", "delivered"] },
    { name: "package-open", icon: PackageOpen, keywords: ["package", "open", "unbox"] },
    // People / Business
    { name: "user", icon: User, keywords: ["user", "person", "profile"] },
    { name: "users", icon: Users, keywords: ["users", "team", "group"] },
    { name: "user-check", icon: UserCheck, keywords: ["user", "verified", "approved"] },
    { name: "handshake", icon: Handshake, keywords: ["handshake", "deal", "opportunity", "agreement"] },
    { name: "building-2", icon: Building2, keywords: ["building", "company", "office"] },
    // Status
    { name: "check", icon: Check, keywords: ["check", "done", "approved"] },
    { name: "check-circle", icon: CheckCircle, keywords: ["check", "circle", "success"] },
    { name: "alert-triangle", icon: AlertTriangle, keywords: ["alert", "warning", "caution"] },
    { name: "info", icon: Info, keywords: ["info", "information", "details"] },
    { name: "x-circle", icon: XCircle, keywords: ["error", "cancel", "close"] },
    // Tags / Markers
    { name: "tag", icon: Tag, keywords: ["tag", "label", "category"] },
    { name: "bookmark", icon: Bookmark, keywords: ["bookmark", "save", "favorite"] },
    { name: "star", icon: Star, keywords: ["star", "favorite", "rating"] },
    { name: "flag", icon: Flag, keywords: ["flag", "mark", "report"] },
    // Actions
    { name: "search", icon: Search, keywords: ["search", "find", "look"] },
    { name: "eye", icon: Eye, keywords: ["eye", "view", "visible"] },
    { name: "lock", icon: Lock, keywords: ["lock", "secure", "private"] },
    { name: "scan", icon: Scan, keywords: ["scan", "qr", "barcode"] },
    // Time
    { name: "calendar", icon: Calendar, keywords: ["calendar", "date", "schedule"] },
    { name: "clock", icon: Clock, keywords: ["clock", "time", "duration"] },
    // Location
    { name: "globe", icon: Globe, keywords: ["globe", "world", "international"] },
    { name: "map-pin", icon: MapPin, keywords: ["map", "pin", "location"] },
    // Media
    { name: "camera", icon: Camera, keywords: ["camera", "photo", "capture"] },
    { name: "image", icon: Image, keywords: ["image", "photo", "picture"] },
    // AI / Special
    { name: "sparkles", icon: Sparkles, keywords: ["sparkles", "ai", "magic", "new"] },
    { name: "zap", icon: Zap, keywords: ["zap", "fast", "lightning", "power"] },
    { name: "lightbulb", icon: Lightbulb, keywords: ["lightbulb", "idea", "insight"] },
    // Arrows
    { name: "arrow-down-to-line", icon: ArrowDownToLine, keywords: ["download", "import", "receive"] },
    { name: "arrow-up-from-line", icon: ArrowUpFromLine, keywords: ["upload", "export", "send"] },
];

/** Map icon name → component for rendering. */
const ICON_MAP = new Map<string, LucideIcon>(
    ICON_CATALOG.map((e) => [e.name, e.icon]),
);

export function getIconComponent(name: string): LucideIcon | undefined {
    return ICON_MAP.get(name);
}

interface IconPickerProps {
    label?: string;
    description?: string;
    error?: string;
    value: string;
    onChange: (iconName: string) => void;
    className?: string;
}

export function IconPicker({ label, description, error, value, onChange, className }: IconPickerProps): ReactElement {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");

    const filtered = useMemo(() => {
        if (!search.trim()) return ICON_CATALOG;
        const q = search.toLowerCase();
        return ICON_CATALOG.filter(
            (e) => e.name.includes(q) || e.keywords.some((k) => k.includes(q)),
        );
    }, [search]);

    const SelectedIcon = ICON_MAP.get(value) ?? File;

    const picker = (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className={cn("gap-2 h-9", className)}>
                    <SelectedIcon className="h-4 w-4" />
                    <span className="text-xs text-muted-foreground">{value}</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-3" align="start">
                <Input
                    placeholder="Search icons..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="mb-2 h-8 text-sm"
                    autoFocus
                />
                <div className="grid grid-cols-8 gap-1 max-h-[240px] overflow-y-auto">
                    {filtered.map((entry) => {
                        const Icon = entry.icon;
                        return (
                            <button
                                key={entry.name}
                                type="button"
                                title={entry.name}
                                onClick={() => { onChange(entry.name); setOpen(false); setSearch(""); }}
                                className={cn(
                                    "flex items-center justify-center rounded-md p-2 transition-colors",
                                    value === entry.name
                                        ? "bg-primary text-primary-foreground"
                                        : "hover:bg-muted text-muted-foreground hover:text-foreground",
                                )}
                            >
                                <Icon className="h-4 w-4" />
                            </button>
                        );
                    })}
                    {filtered.length === 0 && (
                        <p className="col-span-8 py-4 text-center text-xs text-muted-foreground">No icons found</p>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );

    if (label) {
        return (
            <LabelField label={label} description={description} error={error}>
                {picker}
            </LabelField>
        );
    }

    return picker;
}
